import React, { useEffect, useState, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import BookCard from '../components/BookCard';
import SearchBar from '../components/SearchBar';
import { apiUrl } from '../api';

const LANGUAGES = [
  { code: '', label: 'All Languages' },
  { code: 'en', label: 'English' },
  { code: 'fr', label: 'French' },
  { code: 'de', label: 'German' },
  { code: 'es', label: 'Spanish' },
  { code: 'it', label: 'Italian' },
  { code: 'pt', label: 'Portuguese' },
  { code: 'nl', label: 'Dutch' },
  { code: 'fi', label: 'Finnish' },
  { code: 'zh', label: 'Chinese' },
  { code: 'ja', label: 'Japanese' },
];

const SOURCES = [
  { id: 'gutenberg', label: 'Classic Library' },
  { id: 'openlibrary', label: 'Extended Collection' },
];

function SkeletonCard() {
  return (
    <div className="card overflow-hidden animate-pulse">
      <div className="skeleton-shimmer relative" style={{ paddingTop: '140%' }}>
        <div className="absolute inset-0 bg-primary/50" />
      </div>
      <div className="p-4 space-y-3">
        <div className="h-3 skeleton rounded w-1/3" />
        <div className="h-4 skeleton rounded w-full" />
        <div className="h-4 skeleton rounded w-3/4" />
        <div className="h-3 skeleton rounded w-1/2" />
        <div className="h-8 skeleton rounded-lg w-full mt-2" />
      </div>
    </div>
  );
}

function Search() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const query = searchParams.get('q') || '';
  const pageParam = parseInt(searchParams.get('page') || '1', 10);
  const langParam = searchParams.get('lang') || '';
  const sourceParam = searchParams.get('source') || 'gutenberg';

  const [results, setResults] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(pageParam);
  const [selectedLang, setSelectedLang] = useState(langParam);
  const [selectedSource, setSelectedSource] = useState(sourceParam);

  const BOOKS_PER_PAGE = 20;
  const totalPages = Math.ceil(totalCount / BOOKS_PER_PAGE);

  const updateUrl = useCallback(
    (newPage, newLang, newSource) => {
      const params = new URLSearchParams();
      if (query) params.set('q', query);
      if (newPage > 1) params.set('page', String(newPage));
      if (newLang) params.set('lang', newLang);
      if (newSource !== 'gutenberg') params.set('source', newSource);
      navigate(`/search?${params.toString()}`, { replace: true });
    },
    [query, navigate]
  );

  useEffect(() => {
    setCurrentPage(pageParam);
    setSelectedLang(langParam);
    setSelectedSource(sourceParam);
  }, [pageParam, langParam, sourceParam]);

  useEffect(() => {
    if (!query.trim()) {
      setResults([]);
      setTotalCount(0);
      return;
    }

    let cancelled = false;

    const fetchResults = async () => {
      setLoading(true);
      setError(null);

      try {
        let data;
        if (selectedSource === 'openlibrary') {
          const res = await fetch(apiUrl(`/api/books/openlibrary/search?q=${encodeURIComponent(query)}&page=${currentPage}`));
          data = await res.json();
        } else {
          const params = new URLSearchParams({ q: query, page: currentPage });
          if (selectedLang) params.set('lang', selectedLang);
          const res = await fetch(apiUrl(`/api/books/search?${params.toString()}`));
          data = await res.json();
        }

        if (!cancelled) {
          setResults(data.results || []);
          setTotalCount(data.count || 0);
        }
      } catch (err) {
        if (!cancelled) {
          console.error('Search error:', err.message);
          setError('Search failed. Please try again.');
          setResults([]);
          setTotalCount(0);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchResults();
    return () => { cancelled = true; };
  }, [query, currentPage, selectedLang, selectedSource]);

  const handleLangChange = (lang) => {
    setSelectedLang(lang);
    setCurrentPage(1);
    updateUrl(1, lang, selectedSource);
  };

  const handleSourceChange = (source) => {
    setSelectedSource(source);
    setCurrentPage(1);
    setSelectedLang('');
    updateUrl(1, '', source);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    updateUrl(page, selectedLang, selectedSource);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Pagination: show up to 7 page buttons
  const getPageNumbers = () => {
    if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1);
    const pages = [];
    pages.push(1);
    if (currentPage > 4) pages.push('...');
    for (
      let i = Math.max(2, currentPage - 2);
      i <= Math.min(totalPages - 1, currentPage + 2);
      i++
    ) {
      pages.push(i);
    }
    if (currentPage < totalPages - 3) pages.push('...');
    pages.push(totalPages);
    return pages;
  };

  return (
    <div className="py-10">
      <div className="page-container">
        {/* Search header */}
        <div className="mb-8">
          <h1 className="section-title mb-6">
            {query ? `Results for "${query}"` : 'Search Books'}
          </h1>
          <SearchBar initialValue={query} />
        </div>

        {/* Filters row */}
        {query && (
          <div className="flex flex-wrap items-center gap-3 mb-8 pb-6 border-b border-border">
            {/* Source tabs */}
            <div className="flex items-center gap-1 bg-primary rounded-lg p-1">
              {SOURCES.map((src) => (
                <button
                  key={src.id}
                  onClick={() => handleSourceChange(src.id)}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-all duration-200 ${
                    selectedSource === src.id
                      ? 'bg-accent text-white shadow-sm'
                      : 'text-gray-400 hover:text-white'
                  }`}
                >
                  {src.label}
                </button>
              ))}
            </div>

            {/* Language filter — only for Gutenberg */}
            {selectedSource === 'gutenberg' && (
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-gray-500 text-sm">Language:</span>
                <div className="flex flex-wrap gap-1.5">
                  {LANGUAGES.map((lang) => (
                    <button
                      key={lang.code}
                      onClick={() => handleLangChange(lang.code)}
                      className={`px-3 py-1 rounded-full text-xs font-medium transition-all duration-200 ${
                        selectedLang === lang.code
                          ? 'bg-accent text-white'
                          : 'bg-primary border border-border text-gray-400 hover:border-accent/60 hover:text-white'
                      }`}
                    >
                      {lang.label}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Result count */}
            {!loading && totalCount > 0 && (
              <span className="text-gray-500 text-sm ml-auto">
                {totalCount.toLocaleString()} results
              </span>
            )}
          </div>
        )}

        {/* Error state */}
        {error && (
          <div className="rounded-xl border border-red-800/50 bg-red-900/10 p-6 text-center mb-8">
            <p className="text-red-400 font-medium">{error}</p>
          </div>
        )}

        {/* Empty query prompt */}
        {!query && (
          <div className="text-center py-24 text-gray-500">
            <span className="text-6xl block mb-4">🔍</span>
            <p className="text-lg">Start typing to search for books</p>
            <p className="text-sm mt-2">Try "Sherlock Holmes", "Jane Austen", or "Philosophy"</p>
          </div>
        )}

        {/* Loading skeletons */}
        {loading && (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-5">
            {Array.from({ length: 20 }).map((_, i) => (
              <SkeletonCard key={i} />
            ))}
          </div>
        )}

        {/* Results grid */}
        {!loading && results.length > 0 && (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-5">
              {results.map((book) => (
                <BookCard key={`${book.source}-${book.id}`} book={book} />
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-2 mt-12 flex-wrap">
                {/* Prev button */}
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="flex items-center gap-1 px-4 py-2 rounded-lg border border-border
                             text-sm text-gray-400 hover:text-white hover:border-accent/60
                             disabled:opacity-40 disabled:cursor-not-allowed transition-all duration-200"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                  Prev
                </button>

                {/* Page numbers */}
                {getPageNumbers().map((page, idx) =>
                  page === '...' ? (
                    <span key={`ellipsis-${idx}`} className="px-2 text-gray-600">
                      ...
                    </span>
                  ) : (
                    <button
                      key={page}
                      onClick={() => handlePageChange(page)}
                      className={`w-10 h-10 rounded-lg text-sm font-medium transition-all duration-200 ${
                        currentPage === page
                          ? 'bg-accent text-white shadow-lg shadow-accent/30'
                          : 'border border-border text-gray-400 hover:text-white hover:border-accent/60'
                      }`}
                    >
                      {page}
                    </button>
                  )
                )}

                {/* Next button */}
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="flex items-center gap-1 px-4 py-2 rounded-lg border border-border
                             text-sm text-gray-400 hover:text-white hover:border-accent/60
                             disabled:opacity-40 disabled:cursor-not-allowed transition-all duration-200"
                >
                  Next
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </div>
            )}
          </>
        )}

        {/* No results */}
        {!loading && query && results.length === 0 && !error && (
          <div className="text-center py-24 text-gray-500">
            <span className="text-6xl block mb-4">📭</span>
            <p className="text-lg text-white mb-2">No results found for "{query}"</p>
            <p className="text-sm mb-6">
              Try a different spelling, or switch to the other source
            </p>
            <div className="flex items-center justify-center gap-4">
              {SOURCES.filter((s) => s.id !== selectedSource).map((src) => (
                <button
                  key={src.id}
                  onClick={() => handleSourceChange(src.id)}
                  className="btn-secondary"
                >
                  Try {src.label}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default Search;
